import authApi from '_api/auth'
import axiosClient from '_api/axiosClient'
import userApi from '_api/user'
import { useModal } from '_hooks/useModal'
import { GOOGLE } from '_utils/constant'
import { arrayToString, parseJwt } from '_utils/function'
import { setSocialToken, setUserToken } from '_utils/localData'
import { useState } from 'react'
import { useToasts } from 'react-toast-notifications'

export const useShareLogin = ({
  guestFormData,
  submission,
  setUserInfo,
  closeNotifyPopup,
}) => {
  const { addToast } = useToasts()
  const [isLoginSubmitting, setIsLoginSubmitting] = useState(false)

  const {
    isOpen: isLoginModalOpen,
    openModal: openLoginModal,
    closeModal: closeLoginModal,
  } = useModal()

  const handleLoginSuccess = async (token) => {
    setUserToken(token)
    const parsedUserInfo = parseJwt(token)
    axiosClient.defaults.headers.common.Authorization = `Bearer ${token}`

    try {
      const res = await userApi.getUserById(parsedUserInfo?.id)
      const fullUserInfo = res?.msgResp || parsedUserInfo
      setUserInfo(fullUserInfo)
    } catch (err) {
      setUserInfo(parsedUserInfo)
    }

    if (guestFormData?.noteToHost) {
      submission.setComment(guestFormData.noteToHost)
    }
    closeLoginModal()
  }

  const handleLogin = async ({ email, pin }) => {
    try {
      setIsLoginSubmitting(true)
      const res = await authApi.login({
        email: email.toLowerCase().trim(),
        password: arrayToString(pin).toLowerCase().trim(),
      })

      const token = res?.msgResp?.token
      if (!token) {
        addToast('Login failed', { appearance: 'error', autoDismiss: true })
        return
      }

      await handleLoginSuccess(token)
    } catch (err) {
      if (Object.keys(err?.msgResp || {}).length === 1) {
        addToast(`Left retries ${err.msgResp.leftRetries}`, { appearance: 'error', autoDismiss: true })
      } else {
        addToast(err?.msgResp || 'Login failed', { appearance: 'error', autoDismiss: true })
      }
    } finally {
      setIsLoginSubmitting(false)
    }
  }

  const handleLoginGoogle = async (googleResponse) => {
    try {
      setIsLoginSubmitting(true)
      const accessToken = googleResponse?.accessToken
      if (!accessToken) {
        addToast('Google login failed', { appearance: 'error', autoDismiss: true })
        return
      }

      const res = await authApi.login3rdParty({ type: GOOGLE, accessToken })
      const token = res?.msgResp?.token
      if (!token) {
        addToast('Google login failed', { appearance: 'error', autoDismiss: true })
        return
      }

      setSocialToken(accessToken)
      await handleLoginSuccess(token)
    } catch (err) {
      addToast(err?.msgResp || 'Google login failed', { appearance: 'error', autoDismiss: true })
    } finally {
      setIsLoginSubmitting(false)
    }
  }

  const handleOpenLoginFromNotify = () => {
    if (closeNotifyPopup) closeNotifyPopup()
    openLoginModal()
  }

  return {
    isLoginModalOpen,
    openLoginModal,
    closeLoginModal,
    isLoginSubmitting,
    handleLogin,
    handleLoginGoogle,
    handleOpenLoginFromNotify,
  }
}
